import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { COACHES } from "@/data/coaches";
import { AdminCoachRow } from "@/components/admin/AdminCoachRow";

function useNoIndex() {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.setAttribute("name", "robots");
    tag.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(tag);
    const prevTitle = document.title;
    document.title = "Admin · Coaches | GolfStart";
    return () => {
      tag.remove();
      document.title = prevTitle;
    };
  }, []);
}

function AdminLogin({ onSuccess }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/login", { key });
      setKey("");
      onSuccess();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-24 max-w-md border border-cream-border bg-cream-light p-8 md:p-10">
      <Lock className="h-5 w-5 text-gold" strokeWidth={1.5} />
      <h1 className="mt-5 font-serif text-4xl text-forest">Admin access</h1>
      <p className="mt-3 font-sans text-sm text-forest-muted">Enter your GolfStart admin key to manage coaches.</p>
      <input
        type="password"
        autoComplete="current-password"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        data-testid="admin-key-input"
        placeholder="Admin key"
        className="mt-6 w-full border border-cream-border bg-cream px-4 py-3 font-sans text-sm text-forest outline-none focus:border-gold"
      />
      {error && <p data-testid="admin-login-error" className="mt-3 font-sans text-sm text-red-700">{error}</p>}
      <button type="submit" disabled={busy || !key} data-testid="admin-login-submit" className="btn-primary mt-6 w-full disabled:opacity-60">
        {busy ? "Checking…" : "Unlock"}
      </button>
    </form>
  );
}

export default function AdminCoaches() {
  useNoIndex();
  const [session, setSession] = useState(null);
  const [records, setRecords] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coaches");
      setRecords(Object.fromEntries(data.map((r) => [r.coach_id, r])));
      setSession(true);
    } catch {
      setSession(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    await api.post("/admin/logout").catch(() => {});
    setRecords({});
    setSession(false);
    toast.success("Signed out");
  };

  return (
    <div className="min-h-screen bg-cream px-5 pb-24 md:px-12" data-testid="admin-coaches-page">
      {session === null && <p className="pt-24 text-center font-sans text-sm text-forest-muted">Loading…</p>}
      {session === false && <AdminLogin onSuccess={load} />}
      {session === true && (
        <div className="mx-auto max-w-5xl pt-14">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-cream-border pb-6">
            <div>
              <div className="eyebrow">GolfStart Admin</div>
              <h1 className="mt-3 font-serif text-4xl md:text-5xl text-forest">Coaches</h1>
            </div>
            <button type="button" onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.2em] uppercase text-forest hover:text-gold">
              <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
            </button>
          </div>
          <div className="mt-8 space-y-6">
            {COACHES.map((c) => (
              <AdminCoachRow
                key={c.id}
                coach={c}
                record={records[c.id]}
                onSaved={(r) => setRecords((prev) => ({ ...prev, [c.id]: { ...prev[c.id], ...r } }))}
                onSessionExpired={() => setSession(false)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
